const DEFAULT_RPC_TIMEOUT_MS = 15000;
const MAX_HISTORY_ENTRIES = 8;
const MAX_HISTORY_STRING_LENGTH = 1000;

const EXPLICIT_BROWSER_PREFIX = /^browser-use\b[\s:,-]*/i;
const SENSITIVE_OBSERVATION_KEYS =
  /^(?:screenshot|image|dom|html|accessibilityTree|pageContent|rawObservation)$/i;
const EXECUTOR_ACTION_FIELDS = {
  navigate: ["url"],
  click: ["ref"],
  type: ["ref", "text"],
  key: ["keys"],
  scroll: ["direction", "amount"],
  wait: ["ms"],
};

export const getLocalBrowserTaskIntent = (prompt) => {
  const normalizedPrompt = typeof prompt === "string" ? prompt.trim() : "";
  if (!normalizedPrompt) {
    return { isLocalBrowserTask: false, goal: "" };
  }

  if (EXPLICIT_BROWSER_PREFIX.test(normalizedPrompt)) {
    return {
      isLocalBrowserTask: true,
      goal: normalizedPrompt.replace(EXPLICIT_BROWSER_PREFIX, "").trim(),
    };
  }

  return {
    isLocalBrowserTask: false,
    goal: normalizedPrompt,
  };
};

const sanitizeHistoryValue = (value, seen = new WeakSet()) => {
  if (typeof value === "string") {
    return value.slice(0, MAX_HISTORY_STRING_LENGTH);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return "[circular]";
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.slice(0, 25).map((item) => sanitizeHistoryValue(item, seen));
  }

  return Object.entries(value).reduce((sanitized, [key, entryValue]) => {
    if (!SENSITIVE_OBSERVATION_KEYS.test(key)) {
      sanitized[key] = sanitizeHistoryValue(entryValue, seen);
    }

    return sanitized;
  }, {});
};

export const appendLocalBrowserHistory = (history, action, result) => {
  const nextHistory = [
    ...(Array.isArray(history) ? history : []),
    {
      action: sanitizeHistoryValue(action),
      result: sanitizeHistoryValue(result),
    },
  ];

  return nextHistory.slice(-MAX_HISTORY_ENTRIES);
};

export const normalizeLocalBrowserObservation = (observation) => {
  const source =
    observation && typeof observation === "object" && !Array.isArray(observation)
      ? observation
      : {};
  const nodes = Array.isArray(source.nodes)
    ? source.nodes
    : Array.isArray(source.accessibilityTree)
    ? source.accessibilityTree
    : [];
  const accessibilityTree =
    typeof source.accessibilityTree === "string"
      ? source.accessibilityTree
      : JSON.stringify(nodes);
  const normalized = {
    url: typeof source.url === "string" ? source.url : "",
    title: typeof source.title === "string" ? source.title : "",
    accessibilityTree,
    pageText:
      typeof source.pageText === "string"
        ? source.pageText
        : typeof source.text === "string"
        ? source.text
        : "",
  };

  const screenshotData =
    typeof source.screenshot === "string"
      ? source.screenshot
      : source.screenshot?.data;
  if (typeof screenshotData === "string" && screenshotData) {
    normalized.screenshot = {
      data: screenshotData,
      ...(source.screenshot?.mediaType
        ? { mediaType: source.screenshot.mediaType }
        : {}),
    };
  }
  if (source.lastAction && typeof source.lastAction === "object") {
    normalized.lastAction = source.lastAction;
  }

  return normalized;
};

export const lastActionNeedsVisual = (result) => {
  const last =
    result?.lastAction && typeof result.lastAction === "object"
      ? result.lastAction
      : result;
  if (!last || typeof last !== "object") {
    return false;
  }
  if (last.needsVisual === true || last.ok === false || last.mismatch === true) {
    return true;
  }
  if (last.openedSuggestions === true) {
    return true;
  }

  return Boolean(last.coveredBy);
};

const localBrowserCaptionField = (caption) => {
  if (typeof caption !== "string") {
    return {};
  }
  const sanitized = caption.replace(/[<>]/g, "").replace(/\s+/g, " ").trim().slice(0, 240);
  if (!sanitized) {
    return {};
  }

  return { caption: sanitized };
};

export const buildLocalBrowserRunCommand = (type, runId, details = {}) => {
  if (!runId || typeof runId !== "string") {
    throw new Error("runId is required.");
  }

  if (type === "start") {
    return { type, runId };
  }
  if (type === "stop") {
    return {
      type,
      runId,
      ...(details.keepTab === true ? { keepTab: true } : {}),
      ...(details.keepOverlay === true ? { keepOverlay: true } : {}),
    };
  }
  if (type === "observe") {
    return {
      type,
      runId,
      ...(details.includeScreenshot === true ? { includeScreenshot: true } : {}),
      ...localBrowserCaptionField(details.caption),
    };
  }
  if (type === "execute") {
    return {
      type,
      runId,
      action: details.action,
      ...(details.confirmed === true ? { confirmed: true } : {}),
      ...localBrowserCaptionField(details.caption),
    };
  }
  if (type === "ask") {
    return {
      type,
      runId,
      ...localBrowserCaptionField(details.caption),
      ...(details.ask === false ? { ask: false } : { ask: true }),
      ...(details.handoff === true ? { handoff: true } : {}),
    };
  }

  throw new Error(`Unsupported run command "${type}".`);
};

export const buildLocalBrowserMemoryCommand = (type, host, skill) => {
  if (type === "memory.get") {
    return { type, host };
  }
  if (type === "memory.put") {
    return { type, host, skill };
  }

  throw new Error(`Unsupported memory command "${type}".`);
};

export const getLocalBrowserExecutorAction = (step) => {
  const type = step?.type;
  const fields = EXECUTOR_ACTION_FIELDS[type];
  if (!fields) {
    throw new Error(`Unsupported browser action "${type || ""}".`);
  }

  return fields.reduce(
    (action, field) => {
      if (step[field] !== undefined) {
        action[field] = step[field];
      }

      return action;
    },
    { type }
  );
};

export const CHALLENGE_ALTERNATE_ERROR =
  "This host is challenge-blocked. Navigate to a legitimate alternate public source such as DuckDuckGo, Wikipedia, web.archive.org, or another public directory before asking the user again.";

export const isChallengeAsk = (step) =>
  /captcha|mfa|anti-bot|multi-factor/i.test(
    `${step?.question || ""} ${step?.reason || ""} ${step?.status || ""}`
  );

const CART_GOAL_PATTERN = /\b(add(?:ing)? to (?:my |the )?(?:cart|bag|basket)|(?:cart|bag|basket)\b)/i;
const CART_URL_PATTERN = /\/(?:gp\/)?cart|\/bag|\/basket/i;
const EMPTY_CART_PATTERN =
  /cart is empty|your (?:amazon )?cart is empty|subtotal\s*\(\s*0\s+items?\)|0 items? in (?:your )?(?:cart|bag)/i;
const CART_LINE_ITEM_PATTERN =
  /subtotal\s*\(\s*[1-9]\d*\s+items?\)|[1-9]\d*\s+items?\s+in (?:your )?(?:cart|bag)|qty[:\s]*[1-9]/i;

export const UNVERIFIED_CART_DONE_ERROR =
  "The cart is not verified. Click Add to Cart, open the cart URL, and confirm a line item or item subtotal before calling done. Recommendation tiles are not line items.";

export const ADD_TO_CART_SCROLL_ERROR =
  "Add to Cart is already on this page. Do not scroll. Click that control, then open the cart URL.";

/**
 * Reject planner "done" when the user asked to add something to a cart
 * but the current page is not a filled cart.
 */
export const getUnverifiedCartDoneError = (goal, observation) => {
  if (!CART_GOAL_PATTERN.test(goal || "")) {
    return "";
  }

  const url = typeof observation?.url === "string" ? observation.url : "";
  const text = [
    observation?.title,
    observation?.pageText,
    observation?.accessibilityTree,
  ]
    .filter((value) => typeof value === "string")
    .join(" ");

  if (!CART_URL_PATTERN.test(url)) {
    return UNVERIFIED_CART_DONE_ERROR;
  }
  if (EMPTY_CART_PATTERN.test(text) || !CART_LINE_ITEM_PATTERN.test(text)) {
    return UNVERIFIED_CART_DONE_ERROR;
  }

  return "";
};

const FILING_GOAL_PATTERN = /\b(10-?k|edgar|filing date)\b/i;
const FILING_PAGE_PATTERN = /\/archives\/edgar\/data\/\d+/i;

export const UNVERIFIED_FILING_DONE_ERROR =
  "The 10-K is not open. Open the filing document page (Archives/edgar/data), confirm form type 10-K, and read the filing date from that page before calling done. Search result lists are not enough.";

/**
 * Reject planner "done" when an EDGAR / 10-K goal never opened the filing
 * page. Company search lists show a date, but that is not the document.
 */
export const getUnverifiedFilingDoneError = (goal, observation) => {
  if (!FILING_GOAL_PATTERN.test(goal || "")) {
    return "";
  }

  const url = typeof observation?.url === "string" ? observation.url : "";
  if (!FILING_PAGE_PATTERN.test(url)) {
    return UNVERIFIED_FILING_DONE_ERROR;
  }

  const text = [observation?.title, observation?.pageText, observation?.accessibilityTree]
    .filter((value) => typeof value === "string")
    .join(" ");
  if (!/\b10-?K\b/i.test(text)) {
    return UNVERIFIED_FILING_DONE_ERROR;
  }

  return "";
};

export const getBlockedAddToCartScrollError = (goal, observation, action) => {
  if (action?.type !== "scroll" || !CART_GOAL_PATTERN.test(goal || "")) {
    return "";
  }

  const text = [observation?.pageText, observation?.accessibilityTree]
    .filter((value) => typeof value === "string")
    .join(" ");
  if (!/add to cart/i.test(text)) {
    return "";
  }

  return ADD_TO_CART_SCROLL_ERROR;
};

const COMPARE_GOAL_PATTERN =
  /\b(cheaper|cheapest|compare|which (?:one|is)|find two|vs\.?|versus)\b/i;
const LISTING_URL_PATTERN =
  /\/itm\/\d+|\/dp\/[A-Z0-9]{8,}|\/gp\/product\/[A-Z0-9]+|\/listing\/\d+/i;
const PRICE_PATTERN = /\$\s?[\d,]+(?:\.\d{2})?/g;

export const UNVERIFIED_COMPARE_DONE_ERROR =
  "The comparison is not verified. Open two listing pages, read a price on each page, and name only those listings. Search tiles and other recommended items do not count.";

const normalizeListingUrl = (url) =>
  String(url || "")
    .split("?")[0]
    .replace(/\/$/, "");

const normalizePrice = (value) =>
  String(value || "")
    .replace(/[^\d.]/g, "")
    .replace(/\.00$/, "");

const listingAnchor = (title) => {
  const cleaned = String(title || "")
    .replace(/\s*[|].*$/, "")
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return "";
  }

  const firstWord = words[0].replace(/[^a-z0-9]/gi, "");
  if (firstWord.length >= 5) {
    return firstWord.toLowerCase();
  }

  return words.slice(0, 3).join(" ").toLowerCase();
};

/**
 * Keep unique listing-page observations so a compare-done can be checked
 * against pages the agent actually opened, not search-grid tiles.
 */
export const collectListingObservation = (listings, observation) => {
  const url = typeof observation?.url === "string" ? observation.url : "";
  const next = Array.isArray(listings) ? [...listings] : [];
  if (!LISTING_URL_PATTERN.test(url)) {
    return next;
  }

  const normalizedUrl = normalizeListingUrl(url);
  const entry = {
    url: normalizedUrl,
    title: typeof observation?.title === "string" ? observation.title : "",
    pageText:
      typeof observation?.pageText === "string" ? observation.pageText : "",
  };
  const existing = next.findIndex((item) => item.url === normalizedUrl);
  if (existing >= 0) {
    next[existing] = entry;
  } else {
    next.push(entry);
  }

  return next.slice(-6);
};

/**
 * Reject planner "done" when a cheaper/compare goal names a listing or
 * price that was not observed on an opened listing page.
 */
export const getUnverifiedCompareDoneError = (
  goal,
  observation,
  listings,
  summary
) => {
  if (!COMPARE_GOAL_PATTERN.test(goal || "") || CART_GOAL_PATTERN.test(goal || "")) {
    return "";
  }

  const listingPages = collectListingObservation(
    Array.isArray(listings) ? listings : [],
    observation
  );
  if (listingPages.length < 2) {
    return UNVERIFIED_COMPARE_DONE_ERROR;
  }

  const summaryText = typeof summary === "string" ? summary : "";
  const summaryPrices = summaryText.match(PRICE_PATTERN) || [];
  if (summaryPrices.length < 2) {
    return UNVERIFIED_COMPARE_DONE_ERROR;
  }

  const listingCorpus = listingPages
    .map((item) => `${item.title || ""} ${item.pageText || ""}`)
    .join(" ");
  const listingPriceKeys = new Set(
    (listingCorpus.match(PRICE_PATTERN) || [])
      .map(normalizePrice)
      .filter(Boolean)
  );
  const matchedPrices = summaryPrices.filter((price) =>
    listingPriceKeys.has(normalizePrice(price))
  );
  if (matchedPrices.length < 2) {
    return UNVERIFIED_COMPARE_DONE_ERROR;
  }

  const namedListings = listingPages.filter((item) => {
    const anchor = listingAnchor(item.title);
    return anchor && summaryText.toLowerCase().includes(anchor);
  });
  if (namedListings.length < 2) {
    return UNVERIFIED_COMPARE_DONE_ERROR;
  }

  return "";
};

const EMAIL_DRAFT_GOAL_PATTERN =
  /\b(?:draft|compose)\b.{0,80}\be-?mail\b|\be-?mail to [^\s]+@/i;
const GMAIL_COMPOSE_URL_PATTERN = /mail\.google\.com/i;
const GOAL_EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const COMPOSE_DRAFT_PATTERN =
  /Compose draft:\s*To:\s*([^\n]*)\s*Subject:\s*([^\n]*)\s*Body:\s*([\s\S]{0,800})/i;

export const UNVERIFIED_EMAIL_DRAFT_DONE_ERROR =
  "The email draft is not verified. Open Gmail compose, put the requested address in To (not Subject or Search), and include the requested facts in the subject or body. Do not click Send.";

export const BLOCKED_DRAFT_SEND_ERROR =
  "The goal is to draft the email, not send it. Fill To, Subject, and Message Body, then call done. Do not click Send.";

const observationText = (observation) =>
  [observation?.title, observation?.pageText, observation?.accessibilityTree]
    .filter((value) => typeof value === "string")
    .join(" ");

const emailsFromGoal = (goal) =>
  [...String(goal || "").matchAll(GOAL_EMAIL_PATTERN)].map((match) =>
    match[0].toLowerCase()
  );

/**
 * Reject planner "done" when a draft-email goal never filled Gmail compose.
 */
export const getUnverifiedEmailDraftDoneError = (goal, observation) => {
  if (!EMAIL_DRAFT_GOAL_PATTERN.test(goal || "")) {
    return "";
  }

  const url = typeof observation?.url === "string" ? observation.url : "";
  if (!GMAIL_COMPOSE_URL_PATTERN.test(url) || !/compose=/i.test(url)) {
    return UNVERIFIED_EMAIL_DRAFT_DONE_ERROR;
  }

  const text = observationText(observation);
  const draft = text.match(COMPOSE_DRAFT_PATTERN);
  const toLine = draft ? String(draft[1] || "") : "";
  const subject = draft ? String(draft[2] || "") : "";
  const body = draft ? String(draft[3] || "") : "";
  const requestedEmails = emailsFromGoal(goal);
  const toOk =
    requestedEmails.length === 0 ||
    requestedEmails.some((email) => toLine.toLowerCase().includes(email));
  if (!toOk) {
    return UNVERIFIED_EMAIL_DRAFT_DONE_ERROR;
  }

  const content = `${subject} ${body}`.trim();
  if (!content) {
    return UNVERIFIED_EMAIL_DRAFT_DONE_ERROR;
  }
  if (
    requestedEmails.some((email) => subject.toLowerCase().trim() === email) &&
    !body.trim()
  ) {
    return UNVERIFIED_EMAIL_DRAFT_DONE_ERROR;
  }

  return "";
};

/**
 * Stop the planner from clicking Send when the user only asked for a draft.
 */
export const getBlockedDraftSendError = (goal, step) => {
  if (!EMAIL_DRAFT_GOAL_PATTERN.test(goal || "")) {
    return "";
  }
  if (/\bsend\b/i.test(goal || "") && !/\bdraft\b/i.test(goal || "")) {
    return "";
  }

  const text = `${step?.status || ""} ${step?.confirmationMessage || ""}`;
  if (
    /\bsend(?:ing)? an email\b|\bclick(?:ing)? send\b|\bthis will send\b/i.test(
      text
    )
  ) {
    return BLOCKED_DRAFT_SEND_ERROR;
  }

  return "";
};

export const isRetryableLocalBrowserFailure = (result) => {
  if (
    result?.ok !== false ||
    result?.requiresConfirmation === true ||
    result?.stopped === true
  ) {
    return false;
  }

  const rawError = result?.error;
  const errorMessage =
    typeof rawError === "string"
      ? rawError
      : rawError?.message || rawError?.code || result?.message || "";
  const nonRetryablePattern =
    /origin|cross[- ]origin|pinned|security|unsafe|unauthorized|forbidden|no active browser session|session (?:is )?stopped|tab no longer exists/i;

  return !nonRetryablePattern.test(errorMessage);
};

export const assertLocalBrowserResult = (result) => {
  if (result?.requiresConfirmation === true) {
    return result;
  }
  if (
    (result?.ok === true || result?.success === true) &&
    !result?.error
  ) {
    return result;
  }

  const rawError = result?.error;
  const errorMessage =
    typeof rawError === "string"
      ? rawError
      : rawError?.message ||
        rawError?.code ||
        result?.reason ||
        result?.message ||
        "The browser extension could not complete that action.";
  const isOriginViolation = /origin|pinned/i.test(errorMessage);

  throw new Error(
    isOriginViolation
      ? "The extension blocked a cross-origin browser action. Open the target site and try again."
      : errorMessage
  );
};

export const createLocalBrowserRpc = ({
  targetWindow,
  eventTarget = window,
  timeoutMs = DEFAULT_RPC_TIMEOUT_MS,
  createRequestId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2)}`,
} = {}) => {
  const pendingRequests = new Map();

  const handleMessage = (event) => {
    const message = event?.data;
    if (
      message?.key !== "local-browser-result" ||
      (event?.source && event.source !== targetWindow)
    ) {
      return;
    }

    const requestId = message?.value?.requestId;
    const pending = pendingRequests.get(requestId);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    pendingRequests.delete(requestId);
    pending.resolve(message.value.result);
  };

  eventTarget.addEventListener("message", handleMessage);

  const request = (command, options = {}) =>
    new Promise((resolve, reject) => {
      if (!targetWindow || typeof targetWindow.postMessage !== "function") {
        reject(new Error("The local browser extension is unavailable."));

        return;
      }

      const requestId = createRequestId();
      const cleanup = () => {
        const pending = pendingRequests.get(requestId);
        if (pending) {
          clearTimeout(pending.timeoutId);
          pendingRequests.delete(requestId);
        }
        options.signal?.removeEventListener("abort", handleAbort);
      };
      const handleAbort = () => {
        cleanup();
        reject(new Error("The local browser task was stopped."));
      };
      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error("The local browser extension timed out."));
      }, options.timeoutMs || timeoutMs);

      pendingRequests.set(requestId, {
        timeoutId,
        resolve: (result) => {
          cleanup();
          resolve(result);
        },
        reject,
      });
      options.signal?.addEventListener("abort", handleAbort, { once: true });
      targetWindow.postMessage(
        {
          key: "local-browser-command",
          value: { requestId, command },
        },
        "*"
      );
    });

  const cleanup = () => {
    eventTarget.removeEventListener("message", handleMessage);
    pendingRequests.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error("The local browser connection was closed."));
    });
    pendingRequests.clear();
  };

  return { request, cleanup };
};

const LOCAL_BROWSER_PROBE_TIMEOUT_MS = 2000;

export const probeLocalBrowserAgent = async (
  rpc,
  timeoutMs = LOCAL_BROWSER_PROBE_TIMEOUT_MS
) => {
  if (!rpc || typeof rpc.request !== "function") {
    return false;
  }

  try {
    const result = await rpc.request({ type: "ping" }, { timeoutMs });

    return result?.ok === true && result?.agent === "local-browser";
  } catch (_error) {
    return false;
  }
};

export const requestLocalBrowserStep = async ({
  baseUrl,
  goal,
  organizationId,
  userId,
  idToken,
  observation,
  history,
  memory,
  signal,
  fetchImpl = fetch,
}) => {
  if (
    typeof organizationId !== "string" ||
    !organizationId.trim() ||
    typeof userId !== "string" ||
    !userId.trim() ||
    typeof idToken !== "string" ||
    !idToken.trim()
  ) {
    throw new Error(
      "Your authenticated user or organization context is missing. Reload Addy and try again."
    );
  }

  const response = await fetchImpl(`${baseUrl}/api/agent/browser-step`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      goal,
      organizationId,
      userId,
      observation: normalizeLocalBrowserObservation(observation),
      history,
      memory,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Browser planner request failed (${response.status}).`);
  }

  const payload = await response.json();
  if (payload?.success !== true || !payload.step) {
    throw new Error(payload?.reason || "Browser planner returned an invalid response.");
  }

  return payload.step;
};

export const createConfirmationGate = ({ onRequest, onClear } = {}) => {
  let pendingConfirmation = null;

  const request = (details) => {
    if (pendingConfirmation) {
      return Promise.reject(new Error("A confirmation is already pending."));
    }

    return new Promise((resolve) => {
      pendingConfirmation = { resolve };
      onRequest?.(details);
    });
  };

  const respond = (confirmed) => {
    if (!pendingConfirmation) {
      return false;
    }

    const { resolve } = pendingConfirmation;
    pendingConfirmation = null;
    onClear?.();
    resolve(confirmed === true);

    return true;
  };

  const cancel = () => respond(false);
  const hasPending = () => pendingConfirmation !== null;

  return { request, respond, cancel, hasPending };
};

/**
 * Abort an in-flight browser-use run and dismiss a pending confirmation.
 * The chat loop then tells the extension to stop.
 */
export const abortLocalBrowserTask = (run, confirmation) => {
  if (!run?.controller || run.controller.signal.aborted) {
    return false;
  }

  run.controller.abort();
  confirmation?.cancel?.();

  return true;
};
